<template>
  <div class="c-company-name-auto-complete relative inline-block">
    <BittsInput
      :model-value="query"
      :form-label="formLabel"
      :placeholder="placeholder"
      name="company_name"
      :status="inputStatus"
      :danger-text="lastErrorMessage"
      role="combobox"
      autocomplete="off"
      aria-autocomplete="list"
      :aria-expanded="isListboxVisible"
      :aria-controls="controlledListboxId"
      :aria-activedescendant="activeOptionId"
      @update:model-value="onUserInput"
      @keydown="onKeydown"
    />
    <div class="sr-only" role="status">{{ statusMessage }}</div>
    <div
      v-if="isPopupVisible"
      v-click-away="closeDropdown"
      class="c-company-name-auto-complete__content"
      data-testid="autocomplete-dropdown"
    >
      <ul
        v-if="isListboxVisible"
        :id="listboxId"
        role="listbox"
        aria-label="Company suggestions"
        class="flex flex-col items-stretch"
      >
        <!-- Options never take focus, so the keys live on the combobox input.
             @see https://www.w3.org/WAI/ARIA/apg/patterns/combobox/ (2026-11-02) -->
        <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
        <li
          v-for="(company, index) in companies"
          :id="optionId(index)"
          ref="optionElements"
          :key="index"
          role="option"
          tabindex="-1"
          :aria-selected="index === activeIndex"
          class="c-company-name-auto-complete__option"
          :class="{
            'c-company-name-auto-complete__option--active':
              index === activeIndex,
          }"
          @click="selectCompany(company)"
          @mousedown.prevent
        >
          <BittsAvatar
            :org="{ name: company.name, domain: company.domain }"
            size="small"
            :show-initials="true"
            aria-hidden="true"
          />
          <span>{{ company.name }}</span>
        </li>
      </ul>
      <div v-if="isSearching" class="c-company-name-auto-complete__loading">
        <img
          class="c-company-name-auto-complete__loading-icon"
          src="/images/gifs/loading.gif"
          alt=""
        />
        Searching...
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsAvatar, BittsInput } from '@crossbeam/bitts';

import { debounce } from 'lodash';
import {
  computed,
  onScopeDispose,
  ref,
  useId,
  useTemplateRef,
  watch,
} from 'vue';
import { directive as vClickAway } from 'vue3-click-away';

import { crossbeamApi } from '@/api';
import type { ClearbitAutocompleteItem } from '@/types/search';

type ValidationErrorType = { $message?: string };

const {
  initialValue = '',
  placeholder = 'Company Name',
  formLabel = 'Company Name',
  errors = [],
} = defineProps<{
  initialValue?: string;
  placeholder?: string;
  formLabel?: string;
  errors?: ValidationErrorType[];
}>();

const emit = defineEmits<{
  change: [query: string];
  selected: [company: { name: string; clearbit_domain: string | null }];
}>();

const query = ref(initialValue);
const companies = ref<ClearbitAutocompleteItem[]>([]);
const isSearching = ref(false);
const isDropdownOpen = ref(false);
const hasSearched = ref(false);
const activeIndex = ref(-1);

const listboxId = useId();
const optionElements = useTemplateRef<HTMLElement[]>('optionElements');

const inputStatus = computed(() => (errors.length ? 'danger' : 'default'));
const lastErrorMessage = computed(() => errors.at(-1)?.$message || '');
const isListboxVisible = computed(
  () =>
    isDropdownOpen.value && !isSearching.value && companies.value.length > 0,
);
const isPopupVisible = computed(
  () =>
    isDropdownOpen.value && (isSearching.value || companies.value.length > 0),
);
const controlledListboxId = computed(() =>
  isListboxVisible.value ? listboxId : undefined,
);
const hasActiveOption = computed(
  () => isListboxVisible.value && activeIndex.value >= 0,
);
const activeOptionId = computed(() =>
  hasActiveOption.value ? optionId(activeIndex.value) : undefined,
);
const statusMessage = computed(() => {
  if (isSearching.value) return 'Searching';
  if (!isDropdownOpen.value) return '';
  const count = companies.value.length;
  if (count) return `${count} suggestion${count === 1 ? '' : 's'} available`;
  return hasSearched.value ? 'No suggestions' : '';
});

const searchDebounced = debounce(searchCompanies, 300);

watch(
  () => initialValue,
  (value) => {
    if (value !== query.value) query.value = value;
  },
);

onScopeDispose(() => searchDebounced.cancel());

function optionId(index: number) {
  return `${listboxId}-option-${index}`;
}

function setCompanies(results: ClearbitAutocompleteItem[]) {
  companies.value = results;
  activeIndex.value = -1;
}

function resetSearch() {
  searchDebounced.cancel();
  setCompanies([]);
  isSearching.value = false;
  hasSearched.value = false;
  closeDropdown();
}

function onUserInput(value: string) {
  if (value === query.value) return;
  query.value = value;
  emit('change', value);
  if (!value) {
    resetSearch();
    return;
  }
  openDropdown();
  isSearching.value = true;
  searchDebounced(value);
}

async function searchCompanies(search: string) {
  const { data, error } = await crossbeamApi.GET(
    '/v0.1/clearbit-autocomplete',
    {
      params: { query: { query: search } },
    },
  );
  if (search !== query.value) return;
  // on certain inputs (e.g. " ") clearbit will respond with a 412
  // confirming that the result is an array covers us for later iterating over the result
  setCompanies(!error && Array.isArray(data) ? data : []);
  hasSearched.value = true;
  isSearching.value = false;
}

function onKeydown(event: KeyboardEvent) {
  if (event.isComposing) return;
  if (event.key === 'Tab') {
    closeDropdown();
    return;
  }
  const handlers: Record<string, () => boolean> = {
    ArrowDown: () => moveActiveOption(1),
    ArrowUp: () => moveActiveOption(-1),
    Enter: selectActiveOption,
    Escape: closeOpenDropdown,
  };
  const handler = handlers[event.key];
  if (handler?.()) event.preventDefault();
}

function entryOption(step: number) {
  return step > 0 ? 0 : companies.value.length - 1;
}

function nextOption(step: number) {
  const count = companies.value.length;
  return (activeIndex.value + step + count) % count;
}

function moveActiveOption(step: number) {
  if (isSearching.value) return false;
  if (!companies.value.length) return false;
  const canStepFromActive = hasActiveOption.value;
  openDropdown();
  setActiveOption(canStepFromActive ? nextOption(step) : entryOption(step));
  return true;
}

function setActiveOption(index: number) {
  activeIndex.value = index;
  optionElements.value?.[index]?.scrollIntoView({ block: 'nearest' });
}

function selectActiveOption() {
  if (!hasActiveOption.value) return false;
  const company = companies.value[activeIndex.value];
  if (!company) return false;
  selectCompany(company);
  return true;
}

function closeOpenDropdown() {
  if (!isDropdownOpen.value) return false;
  closeDropdown();
  return true;
}

function openDropdown() {
  isDropdownOpen.value = true;
}

function closeDropdown() {
  isDropdownOpen.value = false;
  activeIndex.value = -1;
}

function selectCompany(company: ClearbitAutocompleteItem) {
  resetSearch();
  emit('selected', { name: company.name, clearbit_domain: company.domain });
  query.value = company.name;
}
</script>
<style lang="pcss" scoped>
@reference "../css/theme.css";
.c-company-name-auto-complete {
  @apply w-full;
}

.c-company-name-auto-complete__content {
  @apply w-full bg-white shadow-overlay mt-2 rounded-bts-sm z-2 absolute overflow-auto;
  max-height: 500px;
}

.c-company-name-auto-complete__loading {
  @apply flex flex-col flex-1 items-center text-center justify-center;
  @apply text-brand-navy text-base min-h-[96px];
}

.c-company-name-auto-complete__loading-icon {
  @apply w-32;
}

.c-company-name-auto-complete__option {
  @apply w-full grid grid-cols-[32px_auto] gap-16 items-center text-left;
  @apply text-brand-navy text-sm whitespace-nowrap px-12 py-6 cursor-pointer;
}

.c-company-name-auto-complete__option--active {
  @apply bg-neutral-background;
}

.c-company-name-auto-complete:focus-within
  .c-company-name-auto-complete__option--active {
  @apply outline-2 -outline-offset-2 outline-neutral-border-focus;
}
</style>
